import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useMatches, useStandings } from '../hooks/useSupabase';
import SafeIcon from '../common/SafeIcon';
import EditableContent from '../components/EditableContent';
import ShareButton from '../components/ShareButton';
import SeasonDashboard from '../components/SeasonDashboard';
import {seasonForDate,isLeagueMatch,isCupMatch,reconcileLeagueStandings} from '../lib/leagueSeason';
import * as FiIcons from 'react-icons/fi';

const { FiCalendar, FiMapPin, FiClock, FiTrendingUp, FiFilter, FiArrowRight } = FiIcons;

const getTeamCategoryLabel = (category) => {
  const normalized = category || 'Senior';
  if (normalized === 'Senior') return "Α' Ομάδα (Senior)";
  const youthMatch = /^K(\d+)$/i.exec(normalized);
  return youthMatch ? `Κ${youthMatch[1]}` : normalized;
};

const sortCategoryValues = (a, b) => {
  if (a === 'Senior') return -1;
  if (b === 'Senior') return 1;
  const aYouth = /^K(\d+)$/i.exec(a);
  const bYouth = /^K(\d+)$/i.exec(b);
  if (aYouth && bYouth) return Number(bYouth[1]) - Number(aYouth[1]);
  return a.localeCompare(b, 'el');
};

const buildCategories = (rows) => Array.from(new Set((rows || []).map((row) => row.team_category || 'Senior').filter(Boolean)))
  .sort(sortCategoryValues)
  .map((value) => ({ value, label: getTeamCategoryLabel(value) }));

const MatchesPage = () => {
  const [activeTab, setActiveTab] = useState('matches');
  const [matchesFilter, setMatchesFilter] = useState('Senior');
  const [competitionFilter, setCompetitionFilter] = useState('all');
  const [standingsFilter, setStandingsFilter] = useState('Senior');
  const [selectedYear, setSelectedYear] = useState('2026-27');

  const { data: matchesData, loading: matchesLoading } = useMatches();
  const { data: standingsData, loading: standingsLoading } = useStandings();

  const matchCategories = useMemo(() => buildCategories(matchesData), [matchesData]);
  const standingsCategories = useMemo(() => buildCategories(standingsData), [standingsData]);

  useEffect(() => {
    if (matchesFilter !== 'all' && !matchCategories.some((item) => item.value === matchesFilter)) setMatchesFilter('all');
  }, [matchCategories, matchesFilter]);

  useEffect(() => {
    if (standingsCategories.length > 0 && !standingsCategories.some((item) => item.value === standingsFilter)) setStandingsFilter(standingsCategories[0].value);
  }, [standingsCategories, standingsFilter]);

  const availableYears = useMemo(() => Array.from(new Set((standingsData || [])
    .filter((row) => (row.team_category || 'Senior') === standingsFilter)
    .map((row) => row.season_year)
    .filter(Boolean))).sort().reverse(), [standingsData, standingsFilter]);

  useEffect(() => {
    if (availableYears.length > 0 && !availableYears.includes(selectedYear)) setSelectedYear(availableYears[0]);
  }, [availableYears, selectedYear]);

  const filteredMatches = useMemo(() => (matchesData || []).filter((match) =>
    (matchesFilter === 'all' || (match.team_category || 'Senior') === matchesFilter) &&
    seasonForDate(match.date) === selectedYear &&
    !['temporary','friendly','academy'].includes(match.source_type) &&
    (competitionFilter === 'all' ||
      (competitionFilter === 'league' && isLeagueMatch(match)) ||
      (competitionFilter === 'cup' && isCupMatch(match)))
  ), [matchesData,matchesFilter,selectedYear,competitionFilter]);
  const filteredStandings = useMemo(() =>
    reconcileLeagueStandings(standingsData,matchesData,standingsFilter,selectedYear),
    [standingsData,matchesData,standingsFilter,selectedYear]);

  const formatDate = (dateString) => dateString ? new Date(`${String(dateString).slice(0, 10)}T12:00:00`).toLocaleDateString('el-GR', { year: 'numeric', month: 'long', day: 'numeric' }) : '';
  const formatTime = (timeString) => timeString ? String(timeString).slice(0, 5) : '';
  const getStatusLabel = (status) => ({ completed: 'Ολοκληρώθηκε', upcoming: 'Επερχόμενος', scheduled: 'Επερχόμενος', live: 'LIVE', half_time: 'Ημίχρονο', second_half: 'LIVE · Β΄ ημίχρονο', canceled: 'Ακυρώθηκε', cancelled: 'Ακυρώθηκε', postponed: 'Αναβλήθηκε' }[status] || status || '');
  const isLiveStatus = (status) => ['live', 'half_time', 'second_half'].includes(status);

  return (
    <div className="min-h-screen bg-gray-50">
      <section className="bg-primary-700 text-white py-16 relative">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4"><EditableContent page="matches" section="header" contentKey="title" defaultValue="Αγωνιστική Δραστηριότητα" tag="span" /></h1>
          <p className="text-xl text-primary-200"><EditableContent page="matches" section="header" contentKey="subtitle" defaultValue="Σεζόν 2026-27 • Α' Ομάδα: Γ' Κατηγορία Γυναικών, 8ος Όμιλος • Κ17: Πανελλήνιο Πρωτάθλημα Νεανίδων, 4ος Όμιλος" tag="span" /></p>
        </div>
        <div className="absolute top-4 right-4"><ShareButton title="Αγωνιστική Δραστηριότητα - Α.Ο. Χανιά" compact variant="outline" /></div>
      </section>

      <section className="bg-slate-50 py-8 md:py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SeasonDashboard defaultCategory="Senior" defaultSeason="2026-27" />
        </div>
      </section>

      <section className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 flex gap-8">
          <button onClick={() => setActiveTab('matches')} className={`py-4 px-2 border-b-2 font-medium ${activeTab === 'matches' ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500'}`}><EditableContent page="matches" section="tabs" contentKey="matches_tab" defaultValue="Αγώνες" tag="span" /></button>
          <button onClick={() => setActiveTab('standings')} className={`py-4 px-2 border-b-2 font-medium ${activeTab === 'standings' ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500'}`}><EditableContent page="matches" section="tabs" contentKey="standings_tab" defaultValue="Βαθμολογία" tag="span" /></button>
        </div>
      </section>

      {activeTab === 'matches' && (
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4">
            <div className="bg-white rounded-lg shadow-md p-4 mb-6 flex flex-wrap items-end gap-4">
              <SafeIcon icon={FiFilter} className="w-5 h-5 text-gray-400 self-center" />
              <label className="text-sm font-medium text-gray-700 flex flex-col gap-1"><span>Κατηγορία</span>
                <select value={matchesFilter} onChange={(e) => setMatchesFilter(e.target.value)} className="border rounded-md py-2 px-3 text-sm">
                  <option value="all">Όλες οι κατηγορίες</option>
                  {matchCategories.map((category) => <option key={category.value} value={category.value}>{category.label}</option>)}
                </select>
              </label>
              <label className="text-sm font-medium text-gray-700 flex flex-col gap-1"><span>Σεζόν</span>
                <select value={selectedYear} onChange={(e)=>setSelectedYear(e.target.value)} className="border rounded-md py-2 px-3 text-sm">
                  {Array.from(new Set([...(matchesData || []).map(row=>seasonForDate(row.date)),...(standingsData || []).map(row=>row.season_year)].filter(Boolean))).sort().reverse().map(year=><option key={year} value={year}>{year}</option>)}
                </select>
              </label>
              <label className="text-sm font-medium text-gray-700 flex flex-col gap-1"><span>Διοργάνωση</span>
                <select value={competitionFilter} onChange={(e)=>setCompetitionFilter(e.target.value)} className="border rounded-md py-2 px-3 text-sm">
                  <option value="all">Όλες οι διοργανώσεις</option>
                  <option value="league">Πρωτάθλημα</option>
                  <option value="cup">Κύπελλο</option>
                </select>
              </label>
              <span className="text-sm text-gray-500 pb-2">{filteredMatches.length} αγώνες</span>
            </div>

            {matchesLoading ? <div className="text-center py-12">Φόρτωση αγώνων...</div> : filteredMatches.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl shadow-lg text-gray-600">Δεν υπάρχουν διαθέσιμοι αγώνες</div>
            ) : (
              <div className="space-y-5">
                {filteredMatches.map((match, index) => (
                  <motion.article key={match.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.04 }} className="bg-white rounded-xl shadow-md hover:shadow-xl transition-shadow overflow-hidden">
                    <Link to={`/matches/${match.id}`} className="block p-6 group">
                      <div className="flex flex-col lg:grid lg:grid-cols-[1fr_2fr_1fr] gap-5 items-center">
                        <div className="w-full flex flex-wrap gap-2 items-center">
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${isLiveStatus(match.status) ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'}`}>{getStatusLabel(match.status)}</span>
                          <span className="px-3 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-800">{getTeamCategoryLabel(match.team_category || 'Senior')}</span>
                          <span className="text-sm text-gray-600 flex items-center"><SafeIcon icon={FiCalendar} className="w-4 h-4 mr-1" />{formatDate(match.date)}</span>
                          {match.time && <span className="text-sm text-gray-600 flex items-center"><SafeIcon icon={FiClock} className="w-4 h-4 mr-1" />{formatTime(match.time)}</span>}
                        </div>

                        <div className="flex items-center justify-center gap-5 text-center">
                          <div className="font-bold text-lg">{match.home_team}</div>
                          <div className="text-2xl font-black text-gray-800">{match.status === 'completed' || isLiveStatus(match.status) ? `${match.home_score ?? 0} - ${match.away_score ?? 0}` : 'VS'}</div>
                          <div className="font-bold text-lg">{match.away_team}</div>
                        </div>

                        <div className="w-full text-right text-sm text-gray-600">
                          {match.location && <div className="flex lg:justify-end items-center"><SafeIcon icon={FiMapPin} className="w-4 h-4 mr-1" />{match.location}</div>}
                          {match.tournament && <div className="mt-2 text-xs font-medium">{match.tournament}{match.round_label ? ' · '+match.round_label : ''}</div>}
                          <div className="mt-3 inline-flex items-center font-black text-primary-600 group-hover:text-primary-800">Match Center <SafeIcon icon={FiArrowRight} className="w-4 h-4 ml-1" /></div>
                        </div>
                      </div>
                    </Link>
                  </motion.article>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {activeTab === 'standings' && (
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4">
            <div className="bg-white rounded-lg shadow-md p-4 mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-4"><SafeIcon icon={FiFilter} className="w-5 h-5 text-gray-400" /><label className="text-sm font-medium">Κατηγορία:</label><select value={standingsFilter} onChange={(e) => setStandingsFilter(e.target.value)} className="border rounded-md py-1 px-3 text-sm">{standingsCategories.map((category) => <option key={category.value} value={category.value}>{category.label}</option>)}</select></div>
              <div className="flex items-center gap-4"><SafeIcon icon={FiCalendar} className="w-5 h-5 text-gray-400" /><label className="text-sm font-medium">Σεζόν:</label><select value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)} className="border rounded-md py-1 px-3 text-sm">{availableYears.map((year) => <option key={year} value={year}>{year}</option>)}</select></div>
            </div>

            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="p-6 bg-primary-50 border-b flex justify-between items-center"><h3 className="text-xl font-bold flex items-center"><SafeIcon icon={FiTrendingUp} className="w-6 h-6 mr-2 text-primary-600" />Βαθμολογία {getTeamCategoryLabel(standingsFilter)} - Σεζόν {selectedYear}{standingsFilter==='Senior'&&selectedYear==='2026-27'?' • 8ος Όμιλος':''}</h3></div>
              {standingsLoading ? <div className="text-center py-12">Φόρτωση βαθμολογίας...</div> : filteredStandings.length === 0 ? <div className="text-center py-12 text-gray-600">Δεν υπάρχουν διαθέσιμα δεδομένα βαθμολογίας.</div> : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50"><tr><th className="px-4 py-3 text-left">Θέση</th><th className="px-4 py-3 text-left">Ομάδα</th><th className="px-4 py-3 text-center">Αγ.</th><th className="px-4 py-3 text-center">Ν</th><th className="px-4 py-3 text-center">Ι</th><th className="px-4 py-3 text-center">Η</th><th className="px-4 py-3 text-center">ΓΥ</th><th className="px-4 py-3 text-center">ΓΚ</th><th className="px-4 py-3 text-center">Βαθ.</th></tr></thead>
                    <tbody className="divide-y">{filteredStandings.map((team) => <tr key={team.id || team.team} className={team.team.includes('ΧΑΝΙΑ') || team.team === 'ΑΟΧ' ? 'bg-primary-50' : ''}><td className="px-4 py-3 font-bold">{team.position}</td><td className="px-4 py-3 font-medium">{team.team}</td><td className="px-4 py-3 text-center">{team.played}</td><td className="px-4 py-3 text-center">{team.won}</td><td className="px-4 py-3 text-center">{team.drawn}</td><td className="px-4 py-3 text-center">{team.lost}</td><td className="px-4 py-3 text-center">{team.gf}</td><td className="px-4 py-3 text-center">{team.ga}</td><td className="px-4 py-3 text-center font-black">{team.points}</td></tr>)}</tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default MatchesPage;
